import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

const About = () => {
  // Use SEO hook for About page
  const { seoData } = useSEO('about', {
    h1: 'Giới thiệu về các bài test tâm lý và trí tuệ',
    metaDescription: 'Tìm hiểu về các loại test tâm lý: IQ, EQ, MBTI, DISC và Holland Code. Ý nghĩa và cách áp dụng trong cuộc sống.',
    title: 'Giới thiệu - Test IQ và các bài test tâm lý'
  });
  const tests = [
    {
      title: 'Test MBTI',
      description: 'Myers-Briggs Type Indicator phân loại 16 loại tính cách dựa trên 4 dimension: Extroversion-Introversion, Sensing-Intuition, Thinking-Feeling, Judging-Perceiving.',
      icon: '🧠',
      color: 'from-blue-500 to-purple-600',
      users: '50K+',
      accuracy: '99%'
    },
    {
      title: 'Test IQ',
      description: 'Intelligence Quotient đo lường khả năng trí tuệ, tư duy logic và khả năng giải quyết vấn đề theo tiêu chuẩn quốc tế.',
      icon: '💡',
      color: 'from-green-500 to-teal-600',
      users: '30K+',
      accuracy: '98%'
    },
    {
      title: 'Test EQ',
      description: 'Emotional Quotient đánh giá trí tuệ cảm xúc, khả năng hiểu và quản lý cảm xúc của bản thân cũng như thấu hiểu người khác.',
      icon: '❤️',
      color: 'from-purple-500 to-pink-600',
      users: '25K+',
      accuracy: '97%'
    },
    {
      title: 'Test DISC',
      description: 'DISC đánh giá phong cách hành vi và giao tiếp qua 4 yếu tố: Dominance, Influence, Steadiness, Conscientiousness.',
      icon: '⚡',
      color: 'from-red-500 to-orange-600',
      users: '20K+',
      accuracy: '96%'
    },
    {
      title: 'Holland Codes',
      description: 'Test Holland xác định sở thích nghề nghiệp dựa trên 6 loại hình: Realistic, Investigative, Artistic, Social, Enterprising, Conventional.',
      icon: '🎯',
      color: 'from-yellow-500 to-orange-500',
      users: '15K+',
      accuracy: '95%'
    }
  ];

  const values = [
    {
      icon: '🌟',
      title: 'Chất Lượng Hàng Đầu',
      description: 'Cam kết cung cấp các bài test chuẩn quốc tế với độ chính xác cao nhất, được kiểm chứng bởi các chuyên gia tâm lý.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: '🔬',
      title: 'Khoa Học & Chính Xác',
      description: 'Dựa trên các nghiên cứu khoa học uy tín và được cập nhật liên tục theo các tiêu chuẩn quốc tế mới nhất.',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: '👥',
      title: 'Trải Nghiệm Người Dùng',
      description: 'Giao diện thân thiện, dễ sử dụng, giúp người dùng có trải nghiệm tốt nhất khi thực hiện các bài test.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: '🛡️',
      title: 'Bảo Mật & Riêng Tư',
      description: 'Thông tin cá nhân được mã hóa SSL 256-bit, tuân thủ GDPR và không chia sẻ với bất kỳ bên thứ ba nào.',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/30 rounded-full -translate-x-48 -translate-y-48 blur-3xl"></div>
          <div className="absolute top-0 right-0 w-80 h-80 bg-purple-200/30 rounded-full translate-x-40 -translate-y-40 blur-3xl"></div>
          <div className="absolute bottom-0 left-1/2 w-64 h-64 bg-pink-200/20 rounded-full -translate-x-32 translate-y-32 blur-2xl"></div>
        </div>

        <div className="relative container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 mb-8">
              <span className="text-2xl mr-2">🏢</span>
              <span className="text-gray-700 font-semibold">Về chúng tôi</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                QuizTest
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
              Nền tảng trắc nghiệm tâm lý hàng đầu Việt Nam, giúp bạn khám phá 
              <br className="hidden md:block" />
              bản thân và phát triển tiềm năng một cách khoa học
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
              {[
                { number: '100K+', label: 'Người dùng', icon: '👥' },
                { number: '500K+', label: 'Bài test', icon: '📊' },
                { number: '99%', label: 'Hài lòng', icon: '⭐' },
                { number: '2024', label: 'Thành lập', icon: '🚀' }
              ].map((stat, index) => (
                <div key={index} className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 text-center border border-gray-200">
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/test-mbti"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <span>Thử ngay Test MBTI</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <a 
                href="#tests"
                className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-blue-500 hover:text-blue-600 transition-all duration-300"
              >
                Tìm hiểu thêm
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left - Content */}
              <div>
                <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                  💡 Sứ mệnh của chúng tôi
                </div>
                <h2 className="text-4xl font-bold text-gray-800 mb-6">
                  Giúp Bạn Khám Phá 
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Bản Thân</span>
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  QuizTest được tạo ra với sứ mệnh giúp mọi người hiểu rõ hơn về bản thân thông qua các bài trắc nghiệm 
                  tâm lý chuẩn quốc tế. Chúng tôi tin rằng việc hiểu về tính cách, khả năng và tiềm năng là nền tảng 
                  quan trọng để phát triển cá nhân và đạt được thành công.
                </p>
                <div className="space-y-4">
                  {[
                    'Cung cấp bài test chuẩn quốc tế với độ chính xác cao',
                    'Báo cáo kết quả chi tiết và dễ hiểu',
                    'Bảo mật thông tin cá nhân tuyệt đối',
                    'Hỗ trợ 24/7 cho người dùng'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Visual */}
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl p-8 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                  
                  <div className="relative z-10">
                    <div className="text-center mb-8">
                      <div className="text-6xl mb-4">🎯</div>
                      <h3 className="text-2xl font-bold mb-2">Mục Tiêu Của Chúng Tôi</h3>
                      <p className="text-blue-100">Trở thành nền tảng hàng đầu</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div className="bg-white/20 rounded-xl p-4">
                        <div className="text-2xl font-bold">1M+</div>
                        <div className="text-sm text-blue-100">Người dùng 2025</div>
                      </div>
                      <div className="bg-white/20 rounded-xl p-4">
                        <div className="text-2xl font-bold">10+</div>
                        <div className="text-sm text-blue-100">Loại test mới</div>
                      </div>
                      <div className="bg-white/20 rounded-xl p-4">
                        <div className="text-2xl font-bold">100%</div>
                        <div className="text-sm text-blue-100">Miễn phí</div>
                      </div>
                      <div className="bg-white/20 rounded-xl p-4">
                        <div className="text-2xl font-bold">24/7</div>
                        <div className="text-sm text-blue-100">Hỗ trợ</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tests Section */}
      <div id="tests" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-6">
              🧪 Bộ Sưu Tập Test
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Hệ Thống Test Tâm Lý Toàn Diện
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Từ tính cách đến trí tuệ cảm xúc, khám phá toàn bộ tiềm năng của bạn qua 5 bài test được thiết kế khoa học
            </p>
          </div>

          {/* Comparison Grid Layout */}
          <div className="max-w-7xl mx-auto">
            {/* Header comparison */}
            <div className="hidden lg:block mb-8">
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
                <div className="grid grid-cols-6 gap-4 text-sm font-semibold text-gray-600">
                  <div className="text-left">Loại Test</div>
                  <div className="text-center">Thời gian</div>
                  <div className="text-center">Số câu hỏi</div>
                  <div className="text-center">Độ chính xác</div>
                  <div className="text-center">Phổ biến</div>
                  <div className="text-center">Hành động</div>
                </div>
              </div>
            </div>

            {/* Tests Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* MBTI Test - Featured */}
              <div className="lg:col-span-2">
                <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-8 text-white relative overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-20 h-20 border-2 border-white rounded-full"></div>
                    <div className="absolute top-20 right-20 w-16 h-16 border-2 border-white rounded-full"></div>
                    <div className="absolute bottom-10 left-20 w-12 h-12 border-2 border-white rounded-full"></div>
                    <div className="absolute bottom-20 right-10 w-8 h-8 border-2 border-white rounded-full"></div>
                  </div>
                  
                  <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                    <div className="lg:col-span-2">
                      <div className="flex items-center mb-6">
                        <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 mr-4">
                          <span className="text-4xl">🧠</span>
                        </div>
                        <div>
                          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-black px-4 py-1 rounded-full text-sm font-bold mb-2">
                            ⭐ MOST POPULAR
                          </div>
                          <h3 className="text-3xl md:text-4xl font-bold">Myers-Briggs (MBTI)</h3>
                        </div>
                      </div>
                      
                      <p className="text-white/90 text-lg mb-6 leading-relaxed">
                        Khám phá 1 trong 16 loại tính cách của bạn thông qua bài test được tin dùng bởi hàng triệu người toàn cầu.
                        Hiểu rõ cách bạn tương tác với thế giới và đưa ra quyết định.
                      </p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold">60</div>
                          <div className="text-sm text-white/80">Câu hỏi</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold">15</div>
                          <div className="text-sm text-white/80">Phút</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold">99%</div>
                          <div className="text-sm text-white/80">Chính xác</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                          <div className="text-2xl font-bold">50K+</div>
                          <div className="text-sm text-white/80">Người dùng</div>
                        </div>
                      </div>
                      
                      <Link 
                        to="/test-mbti"
                        className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        Khám phá tính cách của bạn
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                    
                    <div className="hidden lg:block">
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                        <div className="text-center mb-4">
                          <div className="text-6xl mb-2">📊</div>
                          <h4 className="text-xl font-bold">16 Personality Types</h4>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-xs">
                          {['INTJ', 'INTP', 'ENTJ', 'ENTP', 'INFJ', 'INFP', 'ENFJ', 'ENFP', 
                            'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ', 'ISTP', 'ISFP', 'ESTP', 'ESFP'].map(type => (
                            <div key={type} className="bg-white/20 rounded px-2 py-1 text-center font-semibold hover:bg-white/30 transition-colors">
                              {type}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other Tests in 2x2 Grid */}
              {tests.slice(1).map((test, index) => (
                <div key={index} className="group">
                  <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden h-full">
                    <div className={`h-1 bg-gradient-to-r ${test.color}`}></div>
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-14 h-14 bg-gradient-to-r ${test.color} rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          {test.icon}
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500 mb-1">{test.users} người dùng</div>
                          <div className="text-sm font-semibold text-green-600">{test.accuracy} chính xác</div>
                        </div>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">{test.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-6 line-clamp-3">
                        {test.description}
                      </p>
                      
                      {/* Features */}
                      <div className="space-y-2 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>10-15 phút hoàn thành</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Kết quả chi tiết và miễn phí</span>
                        </div>
                      </div>
                      
                      <Link 
                        to={`/test-${test.title.toLowerCase().split(' ')[1] === 'disc' ? 'disc' : test.title.toLowerCase().split(' ')[1] === 'codes' ? 'holland-codes' : test.title.toLowerCase().split(' ')[1]}`}
                        className={`block w-full bg-gradient-to-r ${test.color} text-white font-semibold py-3 px-4 rounded-xl text-center hover:shadow-lg transition-all duration-300 group-hover:scale-105`}
                      >
                        Bắt đầu Test
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom CTA Section */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 text-white text-center">
              <div className="max-w-3xl mx-auto">
                <div className="text-5xl mb-4">🎯</div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Hoàn thành bộ test để có cái nhìn toàn diện</h3>
                <p className="text-gray-300 text-lg mb-8">
                  Kết hợp kết quả từ tất cả các bài test để hiểu sâu hơn về bản thân, từ tính cách đến khả năng và sở thích nghề nghiệp.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/test-mbti"
                    className="px-8 py-4 bg-white text-gray-900 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300"
                  >
                    Bắt đầu với MBTI
                  </Link>
                  <button className="px-8 py-4 border-2 border-white text-white font-bold rounded-xl hover:bg-white hover:text-gray-900 transition-all duration-300">
                    Xem tất cả kết quả
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-6">
              💎 Giá Trị Cốt Lõi
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Những Gì Chúng Tôi Tin Tưởng
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Các giá trị cốt lõi định hướng mọi hoạt động và quyết định của chúng tôi
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100 group-hover:-translate-y-2">
                  <div className="flex items-start space-x-4">
                    <div className={`w-16 h-16 bg-gradient-to-r ${value.gradient} rounded-2xl flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      {value.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-6">
              📞 Liên Hệ
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Liên Hệ Với Chúng Tôi
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Có câu hỏi hoặc cần hỗ trợ? Chúng tôi luôn sẵn sàng giúp đỡ bạn!
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Contact Info */}
              <div>
                <div className="grid grid-cols-1 gap-6">
                  {/* Email */}
                  <div className="group">
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center text-xl text-white flex-shrink-0">
                          ✉️
                        </div>
                        <div className="flex-grow">
                          <h3 className="text-lg font-bold text-gray-800 mb-1">Email</h3>
                          <p className="text-gray-600 font-medium">nguyenhuynhduc120597@gmail.com</p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="group">
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center text-xl text-white flex-shrink-0">
                          📱
                        </div>
                        <div className="flex-grow">
                          <h3 className="text-lg font-bold text-gray-800 mb-1">Hotline</h3>
                          <p className="text-gray-600 font-medium">0927663809</p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="group">
                    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center text-xl text-white flex-shrink-0">
                          📍
                        </div>
                        <div className="flex-grow">
                          <h3 className="text-lg font-bold text-gray-800 mb-1">Địa chỉ</h3>
                          <p className="text-gray-600 font-medium">30 Đường Thị Mười, Phường TTH, Quận 12, TPHCM</p>
                        </div>
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <a
                    href="mailto:testiqmienphi@gmail.com"
                    className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:scale-105"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Gửi Email
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-blue-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </a>
                  <a
                    href="tel:0927663809"
                    className="group relative overflow-hidden bg-white text-blue-600 font-bold py-4 px-6 rounded-xl border-2 border-blue-600 transition-all duration-300 hover:shadow-lg hover:scale-105"
                  >
                    <span className="relative z-10 flex items-center justify-center group-hover:text-white transition-colors duration-300">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Gọi Ngay
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  </a>
                </div>
              </div>

              {/* Illustration/Visual */}
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl p-8 relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200/50 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-purple-200/50 rounded-full translate-y-12 -translate-x-12"></div>
                    
                    <div className="relative z-10 text-center">
                      <div className="text-8xl mb-6">🤝</div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">Sẵn Sàng Hỗ Trợ</h3>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        Đội ngũ chuyên gia tâm lý học của chúng tôi luôn sẵn sàng tư vấn và hỗ trợ bạn 
                        trong hành trình khám phá bản thân.
                      </p>
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          <span>Phản hồi nhanh</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                          <span>Tư vấn miễn phí</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About; 